/**
 * Operator access (docs/operator-access.md): what the platform's own operators (Mojo Up's staff, on
 * Mojo Up AI Cloud) and the platform's own jobs did with an organisation's data, and the break-glass
 * grants an operator opens before looking at a customer's organisation.
 *
 *   POST   /operator/v1/access                              OperatorAccessOpen -> OperatorAccessGrant (platform operators)
 *   GET    /operator/v1/access                              OperatorAccessGrantList (administrators; grants only for operators)
 *   DELETE /operator/v1/access/:grantId                     -> OperatorAccessGrant, closed (platform operators)
 *   GET    /operator/v1/organisations/:organisationId       OperatorOrganisationSummary (an open grant)
 *   GET    /operator/v1/organisations/:organisationId/audit OperatorOrganisationAudit (an open grant)
 *   GET    /admin/v1/operator-access                        OperatorAccessLog (an organisation's administrators)
 *
 * Deployment-wide: the log lives in its own table, outside any organisation, so a grant and its use
 * are recorded whoever reads; each is also written to the organisation's own audit chain.
 */
import { z } from "zod";
import { auditOriginSchema } from "./auditOrigin.js";
export const OPERATOR_ACCESS_PATH = "/operator/v1/access";
export const OPERATOR_ORGANISATIONS_PATH = "/operator/v1/organisations";
export const OPERATOR_ACCESS_LOG_PATH = "/admin/v1/operator-access";
/** The longest a grant stays open: four hours. */
export const OPERATOR_ACCESS_MAX_MINUTES = 240;
/** An operator opens a grant: which organisation (its id or slug), why in their own words, and for how long. */
export const operatorAccessOpenSchema = z.object({
    organisationId: z.string().trim().min(1).max(200),
    reason: z.string().trim().min(10, "Say why in a sentence: the organisation reads it.").max(1000),
    minutes: z.number().int().min(5).max(OPERATOR_ACCESS_MAX_MINUTES),
});
/** Who did it: an operator by id and name, or the platform's own jobs. */
export const operatorPersonSchema = z.object({ id: z.string(), name: z.string() });
/** `open` until it expires or is closed. */
export const operatorAccessStateSchema = z.enum(["open", "expired", "closed"]);
export const OPERATOR_ACCESS_STATE_WORDS = { open: "Open", expired: "Expired", closed: "Closed" };
export const operatorAccessGrantSchema = z.object({
    id: z.string(),
    operator: operatorPersonSchema,
    organisationId: z.string(),
    /** The organisation's name when it is still there. */
    organisationName: z.string().optional(),
    reason: z.string(),
    openedAt: z.string(),
    expiresAt: z.string(),
    closedAt: z.string().optional(),
    closedBy: operatorPersonSchema.optional(),
    state: operatorAccessStateSchema,
});
export const operatorAccessGrantListSchema = z.object({
    /** Open grants and those of the last 30 days, newest first; empty for someone who is not an operator. */
    grants: z.array(operatorAccessGrantSchema),
    /** Whether the caller may open a grant here, and in words why not. */
    mayOpen: z.boolean(),
    why: z.string().optional(),
});
/**
 * One row of the log. `grant`: an operator opened access (its `why`, until `expiresAt`, closed at
 * `closedAt`); `read`: an operator read the organisation under a grant (`what` is the method and
 * path); `job`: the platform's own code looked across every organisation (`what` is its reason;
 * `organisationId` is absent, since it was every organisation's).
 */
export const operatorAccessKindSchema = z.enum(["grant", "read", "job"]);
export const operatorAccessEntrySchema = z.object({
    id: z.string(),
    kind: operatorAccessKindSchema,
    principal: z.enum(["operator", "platform"]),
    operator: operatorPersonSchema.optional(),
    organisationId: z.string().optional(),
    what: z.string(),
    why: z.string().optional(),
    at: z.string(),
    requestId: z.string().optional(),
    grantId: z.string().optional(),
    expiresAt: z.string().optional(),
    closedAt: z.string().optional(),
});
export const operatorAccessLogSchema = z.object({
    /** Newest first. */
    entries: z.array(operatorAccessEntrySchema),
    /** Whether this deployment has platform operators at all (Mojo Up AI Cloud); Enterprise has none, only the platform's jobs. */
    operators: z.boolean(),
    /** The `at` to ask `?before=` with for the next page, when there is one. */
    next: z.string().optional(),
});
/** `GET /admin/v1/operator-access/summary` and `/export` sit beside the log. */
export const OPERATOR_ACCESS_SUMMARY_PATH = `${OPERATOR_ACCESS_LOG_PATH}/summary`;
export const OPERATOR_ACCESS_EXPORT_PATH = `${OPERATOR_ACCESS_LOG_PATH}/export`;
/** Which rows: `staff` is grants and reads together (people), `job` the service's own work. */
export const operatorAccessShowSchema = z.enum(["all", "staff", "grant", "read", "job"]);
/** The log's filters, as query parameters. `q` matches what, why and the person's name (case ignored); `what` is one reason exactly. */
export const operatorAccessQuerySchema = z.object({
    show: operatorAccessShowSchema.default("all"),
    q: z.string().trim().max(200).optional(),
    what: z.string().max(300).optional(),
    since: z.iso.datetime().optional(),
    until: z.iso.datetime().optional(),
    before: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(100),
});
/** One kind of row in a period: how often, first and last. */
export const operatorAccessGroupSchema = z.object({
    kind: operatorAccessKindSchema,
    what: z.string(),
    count: z.number().int(),
    firstAt: z.string(),
    lastAt: z.string(),
});
export const operatorAccessSummarySchema = z.object({
    since: z.string(),
    until: z.string(),
    totals: z.object({ grants: z.number().int(), reads: z.number().int(), jobs: z.number().int(), openNow: z.number().int() }),
    /** Most frequent first. Grants and reads are grouped by what; jobs by their reason. */
    groups: z.array(operatorAccessGroupSchema),
    operators: z.boolean(),
});
/** The most rows one export holds. */
export const OPERATOR_ACCESS_EXPORT_MAX = 10_000;
/** What an operator sees of an organisation under a grant: enough to find their way, nothing it keeps secret. */
export const operatorOrganisationSummarySchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    kind: z.enum(["enterprise", "personal", "team"]),
    createdAt: z.string(),
    machines: z.number().int(),
    members: z.number().int().optional(),
    grant: operatorAccessGrantSchema,
});
export const operatorOrganisationAuditSchema = z.object({
    organisationId: z.string(),
    entries: z.array(z.object({
        ts: z.string(),
        action: z.string(),
        actor: z.object({ kind: z.string(), id: z.string().optional(), principal: z.string().optional() }),
        target: z.string().optional(),
        outcome: z.string(),
        /** Where and how it was done, as the organisation keeps it (auditOrigin.ts). */
        origin: auditOriginSchema.optional(),
    })),
});
//# sourceMappingURL=operatorAccess.js.map